import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Textarea } from './shadcn/textarea'
import { Button } from './shadcn/button'
import { FileIcon, Lightbulb, Link, Plus, Sparkles, Video, X } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './shadcn/tooltip'
import { PopupWrapper } from './PopupWrapper'
import { toast } from 'sonner'
import { Input } from './shadcn/input'
import { pushEnergy } from '@/lib/energyBus'
import { PlumeAura } from '@/components/ui/PlumeField'

interface Props {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  files: File[]
  onFileChange: (files: File[]) => void
  onFileDelete: (fileIndex: number) => void
  urls: string[]
  onUrlAdd: (url: string) => void
  onUrlUpdate: (index: number, url: string) => void
  onUrlDelete: (index: number) => void
  onBrainstormClick?: () => void
  /** Create an empty workspace and skip generation. Only the home page passes
   *  this; every other prompt box is already inside a workspace. */
  onStartBlank?: () => void
  settingsSlot?: ReactNode
  /** Blocks Generate (button and Enter) while typing stays allowed, e.g. while
   *  the brand's moodboard brief is still being generated. */
  submitDisabled?: boolean
}

export function PromptBox({
  value,
  onChange,
  onSubmit,
  files,
  onFileChange,
  onFileDelete,
  urls,
  onUrlAdd,
  onUrlUpdate,
  onUrlDelete,
  onBrainstormClick,
  onStartBlank,
  settingsSlot,
  submitDisabled = false,
}: Props) {
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [showUrlModal, setShowUrlModal] = useState(false)
  const [editingUrlIndex, setEditingUrlIndex] = useState<number | null>(null)

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = '70px'
      textAreaRef.current.style.height = Math.min(textAreaRef.current.scrollHeight, 220) + 'px'
    }
  }, [value])

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files
    if (!selectedFiles) return

    const accepted = await collectValidFiles(Array.from(selectedFiles))
    if (accepted) {
      onFileChange([...files, ...accepted])
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handleSubmit = () => {
    if (submitDisabled) return
    onSubmit()
  }

  const handleUrlSubmit = (url: string) => {
    if (editingUrlIndex !== null) {
      onUrlUpdate(editingUrlIndex, url)
    } else {
      onUrlAdd(url)
    }
    setShowUrlModal(false)
    setEditingUrlIndex(null)
  }

  const handleUrlEdit = (index: number) => {
    setEditingUrlIndex(index)
    setShowUrlModal(true)
  }

  const handleUrlModalClose = () => {
    setShowUrlModal(false)
    setEditingUrlIndex(null)
  }

  const handleFileSelect = () => {
    document.getElementById('file')?.click()
  }

  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    // Image/PDF paste: route pasted files into the same attachment state the
    // file picker uses. Grab them synchronously (before any await) so the event
    // data is still available.
    const pastedFiles = Array.from(e.clipboardData?.files ?? []).filter((file) =>
      PASTEABLE_FILE_TYPES.includes(file.type),
    )

    if (pastedFiles.length > 0) {
      // Prevent the OS file path / filename from also landing in the textarea.
      e.preventDefault()
      const accepted = await collectValidFiles(pastedFiles)
      if (accepted) {
        onFileChange([...files, ...accepted])
      }
      return
    }

    // URL paste (unchanged): detect links in pasted text and add them as context URLs.
    const pastedText = e.clipboardData?.getData('text/plain')

    const urlRegex = /(https:\/\/[^\s]+)|(www\.[^\s]+)/gi

    const pastedUrls = pastedText?.match(urlRegex)

    if (pastedUrls && pastedUrls.length > 0) {
      pastedUrls.forEach((url) => {
        const normalized = url.startsWith('www.') ? `https://${url}` : url
        if (!urls.includes(normalized)) {
          onUrlAdd(normalized)
        }
      })
    }
  }

  return (
    <div className="relative w-full max-w-[700px]">
      <PlumeAura focused={isFocused} />
      <div
        className="border-border hover:border-primary bg-card relative flex w-full flex-col rounded-xl border p-2 transition-colors"
        tabIndex={0}>
        <div className="flex flex-col">
          <Textarea
            ref={textAreaRef}
            value={value}
            onChange={(e) => {
              // Heavier push than the chat box — the topic field is the page's
              // hero, so each keystroke should visibly land on the plume field.
              pushEnergy(0.18)
              onChange(e.target.value)
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            className="bg-card text-foreground placeholder:text-muted-foreground focus:outline-hidden resize-none overflow-auto border-none"
            placeholder="Enter your topic here..."
          />
          {/* Wraps on phones: in one row it pushed Generate off-screen. */}
          <div className="flex w-full flex-wrap items-center justify-between gap-y-2">
            <input
              type="file"
              multiple
              id="file"
              accept="image/jpeg,image/png,image/webp,application/pdf,text/plain,video/mp4,video/quicktime,video/webm"
              className="hidden"
              onChange={handleFileChange}
            />

            <div className="flex items-center gap-1">
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={handleFileSelect}>
                      <Plus className="text-foreground" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>
                      Upload .pdf, images (.jpg, .png, .webp), or videos (.mp4, .mov, .webm)
                    </span>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setShowUrlModal(true)
                      }}>
                      <Link className="text-foreground" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>Add url link, e.g. https://example.com</span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <div className="ml-auto flex items-center gap-2">
              {settingsSlot}
              {onBrainstormClick && (
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" onClick={onBrainstormClick}>
                        <Lightbulb className="text-foreground h-5 w-5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <span>Brainstorm topic ideas</span>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {onStartBlank && (
                <Button variant="outline" className="rounded-full px-4 py-2" onClick={onStartBlank}>
                  Start blank
                </Button>
              )}

              <Button
                variant="primary"
                className="flex items-center gap-2 rounded-full px-4 py-2"
                onClick={handleSubmit}
                disabled={
                  submitDisabled || (!value.trim() && files.length === 0 && urls.length === 0)
                }>
                <Sparkles className="h-4 w-4" />
                Generate
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {files.map((file, index) => (
              <FilePreview
                key={file.name + index}
                file={file}
                onDelete={() => onFileDelete(index)}
                onChangeFile={handleFileSelect}
              />
            ))}
            {urls.map((url, index) => (
              <UrlPreview
                key={url + index}
                url={url}
                onDelete={() => onUrlDelete(index)}
                onEdit={() => handleUrlEdit(index)}
              />
            ))}
          </div>
        </div>
        {showUrlModal && (
          <UrlModal
            initialUrl={editingUrlIndex !== null ? (urls[editingUrlIndex] ?? '') : ''}
            onSubmit={handleUrlSubmit}
            onClose={handleUrlModalClose}
          />
        )}
      </div>
    </div>
  )
}

const FilePreview = ({
  file,
  onDelete,
  onChangeFile,
}: {
  file: File
  onDelete: () => void
  onChangeFile: () => void
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [showPopup, setShowPopup] = useState(false)

  useEffect(() => {
    if (!file || !file.type.startsWith('image/')) return

    const objectUrl = URL.createObjectURL(file)
    setPreviewUrl(objectUrl) // eslint-disable-line react-hooks/set-state-in-effect -- lifecycle tied to objectURL cleanup

    return () => {
      URL.revokeObjectURL(objectUrl)
      setPreviewUrl(null)
    }
  }, [file])

  if (file.type.startsWith('image/')) {
    return (
      <>
        <div className="flex items-center gap-1">
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Preview"
              className="h-7 w-7 cursor-pointer rounded object-cover hover:opacity-80"
              onClick={() => setShowPopup(true)}
            />
          )}
          <X
            className="text-foreground hover:text-muted-foreground h-4 w-4 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
          />
        </div>
        {showPopup && previewUrl && (
          <PopupWrapper onClose={() => setShowPopup(false)}>
            <img
              src={previewUrl}
              alt="Large Preview"
              className="max-h-[80vh] max-w-[80vw] object-contain"
            />
          </PopupWrapper>
        )}
      </>
    )
  }

  if (file.type.startsWith('video/')) {
    return (
      <div className="bg-muted text-foreground flex items-center gap-2 rounded-md px-2 py-1">
        <Video className="h-4 w-4" />
        <span className="text-sm">{file.name}</span>
        <X
          className="hover:text-primary-hover h-4 w-4 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
        />
      </div>
    )
  }

  return (
    <div className="bg-muted text-foreground flex items-center gap-2 rounded-md px-2 py-1">
      <FileIcon className="h-4 w-4" />
      <span
        className="text-foreground hover:text-primary-hover cursor-pointer text-sm"
        onClick={onChangeFile}>
        {file.name}
      </span>
      <X
        className="hover:text-primary-hover h-4 w-4 cursor-pointer"
        onClick={(e) => {
          e.stopPropagation()
          onDelete()
        }}
      />
    </div>
  )
}

const UrlModal = ({
  initialUrl,
  onSubmit,
  onClose,
}: {
  initialUrl: string
  onSubmit: (url: string) => void
  onClose: () => void
}) => {
  const [url, setUrl] = useState(initialUrl)

  const handleSubmit = () => {
    const { isValid, url: validatedUrl, error: validationError } = validateUrl(url)

    if (!isValid) {
      toast.error(validationError)
      return
    }

    onSubmit(validatedUrl)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit()
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  return (
    <PopupWrapper>
      <div className="bg-card w-[400px] rounded-lg p-4">
        <h3 className="text-foreground mb-4 text-lg">Add URL</h3>
        <Input
          className="border-border bg-background text-foreground"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="https://example.com"
        />
        <div className="mt-4 flex justify-between">
          <Button variant="outline" className="text-foreground" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>{initialUrl ? 'Update' : 'Add'}</Button>
        </div>
      </div>
    </PopupWrapper>
  )
}

const UrlPreview = ({
  url,
  onDelete,
  onEdit,
}: {
  url: string
  onDelete: () => void
  onEdit: () => void
}) => {
  const displayUrl = url.length > 30 ? url.substring(0, 30) + '...' : url

  return (
    <div className="bg-muted text-foreground flex items-center gap-2 rounded-md px-2 py-1">
      <Link className="h-4 w-4" />
      <span
        className="text-foreground hover:text-primary-hover cursor-pointer text-sm"
        onClick={onEdit}>
        {displayUrl}
      </span>
      <X
        className="hover:text-primary-hover h-4 w-4 cursor-pointer"
        onClick={(e) => {
          e.stopPropagation()
          onDelete()
        }}
      />
    </div>
  )
}

// File types accepted when pasting directly into the topic box. This is the
// image/PDF subset of the file picker's `accept` list (paste does not honor the
// `accept` attribute, so we filter explicitly).
const PASTEABLE_FILE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf']

// Validate a batch of candidate files against the same rules the file picker
// enforces. Returns the accepted files, or null if a hard limit was hit (in
// which case nothing should be added, matching the picker's abort behavior).
const collectValidFiles = async (candidates: File[]): Promise<File[] | null> => {
  const accepted: File[] = []

  for (const file of candidates) {
    const isVideo = file.type.startsWith('video/')
    const maxSize = isVideo ? 500 * 1024 * 1024 : 32 * 1024 * 1024
    const maxLabel = isVideo ? '500MB' : '32MB'

    if (file.size > maxSize) {
      toast.error(`File ${file.name} is too large. Max size is ${maxLabel}.`)
      return null
    }

    if (isVideo) {
      let duration: number
      try {
        duration = await getVideoDuration(file)
      } catch {
        toast.error(
          `Could not read video ${file.name}. The file may be corrupted or in an unsupported format.`,
        )
        continue
      }
      if (duration > 30 * 60) {
        toast.error(`Video ${file.name} is too long. Max duration is 30 minutes.`)
        return null
      }
    }

    accepted.push(file)
  }

  return accepted
}

const getVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.preload = 'metadata'
    const url = URL.createObjectURL(file)
    video.src = url
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url)
      resolve(video.duration)
    }
    video.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not read video metadata'))
    }
  })
}

const validateUrl = (input: string): { isValid: boolean; url: string; error?: string } => {
  let url = input.trim()

  if (!url) {
    return { isValid: false, url: '', error: 'URL is required' }
  }

  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = `https://${url}`
  }

  try {
    const urlObj = new URL(url)

    if (!urlObj.hostname.includes('.')) {
      return { isValid: false, url: '', error: 'Invalid URL format' }
    }

    return { isValid: true, url: urlObj.toString() }
  } catch {
    return { isValid: false, url: '', error: 'Invalid URL format' }
  }
}
